import React, { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import Autocomplete from './Autocomplete';
import './UserProfile.css';

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

// US States data
const US_STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'
];

// Countries data (major countries)
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
  'Bangladesh', 'Belarus', 'Belgium', 'Brazil', 'Bulgaria', 'Cambodia', 'Canada', 'Chile', 'China',
  'Colombia', 'Croatia', 'Czech Republic', 'Denmark', 'Egypt', 'Estonia', 'Finland', 'France',
  'Georgia', 'Germany', 'Ghana', 'Greece', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran',
  'Iraq', 'Ireland', 'Israel', 'Italy', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kuwait',
  'Latvia', 'Lebanon', 'Lithuania', 'Luxembourg', 'Malaysia', 'Mexico', 'Morocco', 'Netherlands',
  'New Zealand', 'Nigeria', 'Norway', 'Pakistan', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Qatar', 'Romania', 'Russia', 'Saudi Arabia', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa',
  'South Korea', 'Spain', 'Sri Lanka', 'Sweden', 'Switzerland', 'Thailand', 'Turkey', 'Ukraine',
  'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Venezuela', 'Vietnam'
];

// Use case options
const USE_CASE_OPTIONS = [
  'Curiosity / general exploration',
  'Personal decisions (EVs, solar, etc.)',
  'Community advocacy / education',
  'Climate Action Plan development (local gov)',
  'Planning / infrastructure decisions (local gov)',
  'Published reports (gov/consultant/academic)',
  'Grant / funding applications',
  'State / regional program evaluation',
  'Academic research / teaching',
  'Media / journalism',
  'Nonprofit / NGO use',
  'Business / corporate sustainability',
  'Other'
];

const UserProfile: React.FC<UserProfileProps> = ({ isOpen, onClose }) => {
  const { user, signOut, updateProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [firstName, setFirstName] = useState(user?.user_metadata?.first_name || '');
  const [lastName, setLastName] = useState(user?.user_metadata?.last_name || '');
  const [organization, setOrganization] = useState(user?.user_metadata?.organization || '');
  const [userType, setUserType] = useState(user?.user_metadata?.user_type || '');
  const [locationType, setLocationType] = useState(user?.user_metadata?.location_type || '');
  const [specificLocation, setSpecificLocation] = useState(user?.user_metadata?.specific_location || '');
  const [useCases, setUseCases] = useState<string[]>(user?.user_metadata?.use_cases || []);
  const [otherUseCase, setOtherUseCase] = useState(user?.user_metadata?.other_use_case || '');
  const [acceptTerms, setAcceptTerms] = useState(user?.user_metadata?.accept_terms || false);
  const [acceptCommunications, setAcceptCommunications] = useState(user?.user_metadata?.accept_communications !== false);
  
  const [californiaLocations, setCaliforniaLocations] = useState<string[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [userComments, setUserComments] = useState<any[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showComments, setShowComments] = useState(false);

  // Load California locations when component mounts
  useEffect(() => {
    const loadCaliforniaLocations = async () => {
      if (locationType === 'California') {
        setLoadingLocations(true);
        try {
          await supabaseLocationService.initialize();
          const locations = supabaseLocationService.getAvailableLocations();
          const locationNames = locations.map(loc => loc.name).sort();
          setCaliforniaLocations(locationNames);
        } catch (error) {
          console.error('Error loading California locations:', error);
        } finally {
          setLoadingLocations(false);
        }
      }
    };

    loadCaliforniaLocations();
  }, [locationType]);

  // Load user comments when component mounts
  useEffect(() => {
    const loadUserComments = async () => {
      if (!user) return;
      
      setLoadingComments(true);
      try {
        console.log('UserProfile: Loading comments for user:', user.id);
        const { data, error } = await supabaseClient
          .from('community_comments')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error loading user comments:', error);
        } else {
          console.log('UserProfile: Loaded comments:', data?.length || 0, 'comments');
          console.log('UserProfile: Sample comment:', data?.[0]);
          setUserComments(data || []);
        }
      } catch (error) {
        console.error('Error loading user comments:', error);
      } finally {
        setLoadingComments(false);
      }
    };

    loadUserComments();
  }, [user]);

  // Reset specific location when location type changes
  useEffect(() => {
    setSpecificLocation('');
  }, [locationType]);

  // Handle use case checkbox changes
  const handleUseCaseChange = (useCase: string, checked: boolean) => {
    if (checked) {
      setUseCases(prev => [...prev, useCase]);
    } else {
      setUseCases(prev => prev.filter(uc => uc !== useCase));
    }
  };

  // Sanitize and validate other use case input
  const handleOtherUseCaseChange = (value: string) => {
    // Remove excessive whitespace and limit repeated characters
    const sanitized = value
      .trim()
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .replace(/(.)\1{4,}/g, '$1$1$1$1') // Limit repeated characters to max 4
      .replace(/[<>]/g, '') // Remove potential HTML tags
      .substring(0, 100); // Enforce max length
    
    setOtherUseCase(sanitized);
  };

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);

    // Validate required fields
    if (!firstName.trim() || !lastName.trim() || !organization.trim() || !userType || !locationType || !specificLocation || useCases.length === 0) {
      setError('Please fill in all required fields to complete your profile');
      setLoading(false);
      return;
    }

    // Validate terms acceptance
    if (!acceptTerms) {
      setError('You must accept the Terms of Use and Privacy Statement to complete your profile');
      setLoading(false);
      return;
    }

    // Additional validation for "Other" use case
    if (useCases.includes('Other') && (!otherUseCase || otherUseCase.trim().length < 3)) {
      setError('Please provide a description for "Other" use case (minimum 3 characters)');
      setLoading(false);
      return;
    }

    // Check for potential spam patterns
    if (otherUseCase && (
      otherUseCase.length > 50 && 
      (otherUseCase.match(/\b(click|buy|sell|free|money|cash|win|prize|offer|deal|discount|promo|spam|advertisement|advertise)\b/gi) || 
       otherUseCase.match(/[!@#$%^&*()_+={}[\]|\\:";'<>?,./]{3,}/) ||
       otherUseCase.match(/\b(www\.|http|\.com|\.org|\.net)\b/gi))
    )) {
      setError('Please provide a valid use case description');
      setLoading(false);
      return;
    }

    const { error } = await updateProfile({
      first_name: firstName,
      last_name: lastName,
      organization: organization,
      user_type: userType,
      location_type: locationType,
      specific_location: specificLocation,
      use_cases: useCases,
      other_use_case: otherUseCase,
      accept_terms: acceptTerms,
      accept_communications: acceptCommunications
    });

    if (error) {
      setError(error.message);
    } else {
      setSuccess('Profile updated successfully!');
    }

    setLoading(false);
  };

  const handleSignOut = async () => {
    setLoading(true);
    const { error } = await signOut();
    if (error) {
      setError(error.message);
    } else {
      onClose();
    }
    setLoading(false);
  };

  if (!isOpen || !user) return null;

  return (
    <div className="user-profile-overlay" onClick={onClose}>
      <div className="user-profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="user-profile-header">
          <h2>Profile</h2>
          <button className="user-profile-close" onClick={onClose}>×</button>
        </div>

        <div className="user-profile-body">
          <div className="user-profile-message">
            <h3 className="user-profile-message-title">Why Profile is Required for Data Download</h3>
            <p>Help keep ClimatePlans.org free for everyone. CoolClimate and potential funders need to know how ClimatePlans.org contributes to equitable climate action planning throughout California. Thank you! ~CoolClimate Network</p>
          </div>

          {error && (
            <div className="user-profile-error">
              {error}
            </div>
          )}

          {success && (
            <div className="user-profile-success">
              {success}
            </div>
          )}

          <div className="user-profile-info">
              <div className="user-profile-avatar">
                {user.user_metadata?.first_name?.[0] || user.email?.[0] || 'U'}
              </div>
            <div className="user-profile-details">
              <h3>{user.user_metadata?.first_name} {user.user_metadata?.last_name}</h3>
              <p>{user.email}</p>
              <p className="user-profile-org">{user.user_metadata?.organization || 'No organization'}</p>
              {user.user_metadata?.user_type && (
                <p className="user-profile-type">Type: {user.user_metadata.user_type}</p>
              )}
              {user.user_metadata?.location_type && user.user_metadata?.specific_location && (
                <p className="user-profile-location">
                  Location: {user.user_metadata.specific_location}, {user.user_metadata.location_type}
                </p>
              )}
              {((user.user_metadata?.use_cases && user.user_metadata.use_cases.length > 0) || user.user_metadata?.other_use_case) && (
                <div className="user-profile-use-cases">
                  <p className="user-profile-use-cases-title">Use Cases:</p>
                  <div className="user-profile-use-cases-list">
                    {user.user_metadata?.use_cases && user.user_metadata.use_cases.map((useCase: string, index: number) => (
                      <span key={index} className="user-profile-use-case-tag">
                        {useCase}
                      </span>
                    ))}
                    {user.user_metadata?.other_use_case && (
                      <span className="user-profile-use-case-tag user-profile-use-case-other">
                        Other: {user.user_metadata.other_use_case}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <form onSubmit={handleUpdateProfile} className="user-profile-form">
            <h4>Update Profile</h4>
            
            <div className="user-profile-form-row">
              <div className="user-profile-form-group">
                <label htmlFor="firstName">First Name *</label>
                <input
                  id="firstName"
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={loading}
                  placeholder="First name"
                  required
                />
              </div>

              <div className="user-profile-form-group">
                <label htmlFor="lastName">Last Name *</label>
                <input
                  id="lastName"
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={loading}
                  placeholder="Last name"
                  required
                />
              </div>
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="organization">Organization (write "none" if not applicable) *</label>
              <input
                id="organization"
                type="text"
                value={organization}
                onChange={(e) => setOrganization(e.target.value)}
                disabled={loading}
                placeholder="Write 'none' if not applicable"
                required
              />
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="userType">User Type *</label>
              <select
                id="userType"
                value={userType}
                onChange={(e) => setUserType(e.target.value)}
                disabled={loading}
                required
              >
                <option value="">Select user type</option>
                <option value="Resident">Resident</option>
                <option value="Local Government">Local Government</option>
                <option value="State Government">State Government</option>
                <option value="Other Government Organization">Other Government Organization</option>
                <option value="Business">Business</option>
                <option value="Academic">Academic</option>
                <option value="Non-Profit">Non-Profit</option>
                <option value="Consultancy">Consultancy</option>
                <option value="Other">Other</option>
              </select>
            </div>

            <div className="user-profile-form-group">
              <label htmlFor="locationType">My Location *</label>
              <select
                id="locationType"
                value={locationType}
                onChange={(e) => setLocationType(e.target.value)}
                disabled={loading}
                required
              >
                <option value="">Select location type</option>
                <option value="California">California</option>
                <option value="Other US State">Other US State</option>
                <option value="Other Country">Other Country</option>
              </select>
            </div>

            {locationType && (
              <div className="user-profile-form-group">
                <label htmlFor="specificLocation">
                  {locationType === 'California' ? 'California Location' :
                   locationType === 'Other US State' ? 'US State' : 'Country'} *
                </label>
                <Autocomplete
                  id="specificLocation"
                  options={
                    locationType === 'California' ? californiaLocations :
                    locationType === 'Other US State' ? US_STATES.filter(state => state !== 'California') :
                    COUNTRIES.filter(country => country !== 'United States')
                  }
                  value={specificLocation}
                  onChange={setSpecificLocation}
                  placeholder={`Type to search ${locationType === 'California' ? 'locations' : locationType === 'Other US State' ? 'states' : 'countries'}...`}
                  disabled={loading}
                  loading={loadingLocations}
                />
                {loadingLocations && (
                  <div className="user-profile-loading">Loading locations...</div>
                )}
              </div>
            )}

            <div className="user-profile-form-group">
              <label>Use Case *</label>
              <div className="user-profile-checkbox-group">
                {USE_CASE_OPTIONS.map((useCase) => (
                  <label key={useCase} className="user-profile-checkbox-item">
                    <input
                      type="checkbox"
                      checked={useCases.includes(useCase)}
                      onChange={(e) => handleUseCaseChange(useCase, e.target.checked)}
                      disabled={loading}
                    />
                    <span className="user-profile-checkbox-label">{useCase}</span>
                  </label>
                ))}
              </div>
              {useCases.includes('Other') && (
                <div className="user-profile-other-input">
                  <input
                    type="text"
                    value={otherUseCase}
                    onChange={(e) => handleOtherUseCaseChange(e.target.value)}
                    disabled={loading}
                    placeholder="Please specify your use case"
                    className="user-profile-other-text-input"
                    maxLength={100}
                  />
                  <div className={`user-profile-char-count ${
                    otherUseCase.length > 90 ? 'error' : 
                    otherUseCase.length > 80 ? 'warning' : ''
                  }`}>
                    {otherUseCase.length}/100 characters
                  </div>
                </div>
              )}
              <div className="user-profile-checkbox-help">
                Please click all that apply
              </div>
            </div>

            <div className="user-profile-form-group">
              <label className="user-profile-checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptTerms}
                  onChange={(e) => setAcceptTerms(e.target.checked)}
                  disabled={loading}
                  required
                />
                <span className="user-profile-checkbox-text">
                  I accept the{' '}
                  <a 
                    href="https://coolclimate.berkeley.edu/files2/CoolClimate-terms-of-use.pdf" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="user-profile-link"
                  >
                    Terms of Use and Privacy Statement
                  </a>
                </span>
              </label>
            </div>

            <div className="user-profile-form-group">
              <label className="user-profile-checkbox-label">
                <input
                  type="checkbox"
                  checked={acceptCommunications}
                  onChange={(e) => setAcceptCommunications(e.target.checked)}
                  disabled={loading}
                />
                <span className="user-profile-checkbox-text">
                  I agree to receive communications from ClimatePlans. We will not share contact information with third parties.
                </span>
              </label>
            </div>

            <button
              type="submit"
              className="user-profile-button user-profile-button-primary"
              disabled={loading}
            >
              {loading ? 'Updating...' : 'Update Profile'}
            </button>
          </form>

          {/* User Comments Section */}
          <div className="user-profile-comments">
            <div className="user-profile-comments-header">
              <h4>My Comments ({userComments.length})</h4>
              <button
                className="user-profile-comments-toggle"
                onClick={() => setShowComments(!showComments)}
              >
                {showComments ? 'Hide' : 'Show'}
              </button>
            </div>

            {/* Link to full Community page (moved under profile) */}
            <div style={{ marginTop: '8px', marginBottom: '12px' }}>
              <a href="/community" className="user-profile-link" style={{ textDecoration: 'underline' }}>
                View Community discussions and all chart comments →
              </a>
            </div>
            
            {showComments && (
              <div className="user-profile-comments-list">
                {loadingComments ? (
                  <div className="user-profile-comments-loading">
                    Loading comments...
                  </div>
                ) : userComments.length === 0 ? (
                  <div className="user-profile-comments-empty">
                    No comments yet. Start participating in community discussions!
                  </div>
                ) : (
                  userComments.map(comment => (
                    <div key={comment.id} className="user-profile-comment">
                      <div className="user-profile-comment-header">
                        <span className="user-profile-comment-type">
                          {comment.post_type?.replace('_', ' ').toUpperCase() || 'COMMENT'}
                        </span>
                        <span className="user-profile-comment-date">
                          {new Date(comment.created_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="user-profile-comment-content">
                        {comment.content}
                      </div>
                      {comment.chart_context && (
                        <div className="user-profile-comment-context">
                          <strong>Chart:</strong> {comment.chart_context}
                        </div>
                      )}
                      {comment.location_context && (
                        <div className="user-profile-comment-context">
                          <strong>Location:</strong> {comment.location_context}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          <div className="user-profile-actions">
            <button
              className="user-profile-button user-profile-button-secondary"
              onClick={handleSignOut}
              disabled={loading}
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
